import * as Select from "@radix-ui/react-select";

import type { ChainDef } from "../data/tokens.js";

import styles from "../styles/RichSelect.module.css";
import { TokenIcon } from "./TokenIcon.js";

/**
 * Radix-based rich select for chains. Renders the chain logo + name in the
 * trigger and a per-item row with logo + name + native-token symbol meta.
 *
 * Falls back to text initials when `logoURI` is missing.
 */
export interface ChainSelectProps {
  readonly value: ChainDef;
  readonly chains: readonly ChainDef[];
  readonly onChange: (id: number) => void;
  readonly ariaLabel?: string;
}

export function ChainSelect({
  value,
  chains,
  onChange,
  ariaLabel,
}: ChainSelectProps): React.JSX.Element {
  return (
    <Select.Root
      value={String(value.id)}
      onValueChange={(v) => onChange(Number(v))}
    >
      <Select.Trigger
        className={styles.trigger}
        aria-label={ariaLabel ?? "Chain"}
      >
        <TokenIcon logoURI={value.logoURI} symbol={value.shortName} />
        <span className={styles.triggerLabel}>
          <span className={styles.triggerPrimary}>{value.name}</span>
          <span className={styles.triggerSecondary}>
            #{value.id} · {value.tokens.length} tokens
          </span>
        </span>
        <span className={styles.chainBadges}>
          {value.mainnet === false ? (
            <span className={styles.badgeWarn}>Testnet</span>
          ) : null}
        </span>
        <Select.Icon className={styles.triggerChevron}>▾</Select.Icon>
      </Select.Trigger>
      <Select.Portal>
        <Select.Content
          className={styles.content}
          position="popper"
          sideOffset={4}
        >
          <Select.Viewport className={styles.viewport}>
            {chains.map((c) => (
              <Select.Item
                key={c.id}
                value={String(c.id)}
                className={styles.item}
              >
                <TokenIcon
                  logoURI={c.logoURI}
                  symbol={c.shortName}
                  size="item"
                />
                <span className={styles.itemBody}>
                  <Select.ItemText asChild>
                    <span className={styles.itemPrimary}>{c.name}</span>
                  </Select.ItemText>
                  <span className={styles.itemSecondary}>
                    #{c.id} · {c.tokens.length} tokens
                  </span>
                </span>
                <span className={styles.chainBadges}>
                  {c.mainnet === false ? (
                    <span className={styles.badgeWarn}>Testnet</span>
                  ) : null}
                </span>
              </Select.Item>
            ))}
          </Select.Viewport>
        </Select.Content>
      </Select.Portal>
    </Select.Root>
  );
}
